// src/pages/Convites/GeradorCodigosConvite.jsx

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Card,
  CardContent,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Tooltip,
  IconButton,
  Divider,
  FormControlLabel,
  Switch,
  Checkbox,
  ListItemText,
  OutlinedInput
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

const GeradorCodigosConvite = () => {
  const { instituicaoId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  // Estados para gerenciar a geração de códigos
  const [formGerador, setFormGerador] = useState({
    tipoConvite: 'INDIVIDUAL',
    quantidade: 1,
    dataExpiracao: '',
    modulosAcesso: []
  });
  const [modulosDisponiveis, setModulosDisponiveis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingModulos, setLoadingModulos] = useState(false);
  const [loadingConvites, setLoadingConvites] = useState(false);
  
  // Estados para a tabela de convites
  const [convites, setConvites] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalConvites, setTotalConvites] = useState(0);
  const [filtros, setFiltros] = useState({
    status: '',
    usado: ''
  });
  
  // Estados para diálogos
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cancelarDialogOpen, setCancelarDialogOpen] = useState(false);
  const [conviteSelecionado, setConviteSelecionado] = useState(null);
  
  // Efeito para carregar os módulos disponíveis para esta instituição
  useEffect(() => {
    const fetchModulos = async () => {
      try {
        setLoadingModulos(true);
        const response = await api.get(`/modulos/instituicao/${instituicaoId}`);
        
        // Filtrar apenas os módulos ativos
        const modulosAtivos = response.data.filter(m => m.instituicao_ativo);
        setModulosDisponiveis(modulosAtivos);
      } catch (error) {
        console.error('Erro ao carregar módulos:', error);
        enqueueSnackbar('Erro ao carregar módulos disponíveis', { variant: 'error' });
      } finally {
        setLoadingModulos(false);
      }
    };
    
    if (instituicaoId) {
      fetchModulos();
    }
  }, [instituicaoId, enqueueSnackbar]);
  
  // Efeito para carregar os convites existentes
  useEffect(() => {
    const fetchConvites = async () => {
      try {
        setLoadingConvites(true);
        
        // Construir a URL com filtros
        let url = `/convites/instituicao/${instituicaoId}?`;
        if (filtros.status) url += `status=${filtros.status}&`;
        if (filtros.usado !== '') url += `usado=${filtros.usado}&`;
        
        // Adicionar paginação
        url += `page=${page + 1}&limit=${rowsPerPage}`;
        
        const response = await api.get(url);
        
        setConvites(response.data.convites || response.data);
        setTotalConvites(response.data.total || response.data.length);
      } catch (error) {
        console.error('Erro ao carregar convites:', error);
        enqueueSnackbar('Erro ao carregar convites', { variant: 'error' });
      } finally {
        setLoadingConvites(false);
      }
    };
    
    if (instituicaoId) {
      fetchConvites();
    }
  }, [instituicaoId, page, rowsPerPage, filtros, enqueueSnackbar]);
  
  // Handler para mudar página da tabela
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handler para mudar quantidade de linhas por página
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handler para mudanças no formulário de geração
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormGerador(prev => ({ ...prev, [name]: value }));
  };
  
  // Handler específico para a seleção de módulos (múltipla)
  const handleModulosChange = (event) => {
    const { value } = event.target;
    setFormGerador(prev => ({ ...prev, modulosAcesso: value }));
  };
  
  // Função para gerar novos códigos
  const gerarCodigos = async () => {
    try {
      if (formGerador.tipoConvite === 'INDIVIDUAL' && formGerador.quantidade > 50) {
        if (!window.confirm('Você está gerando uma grande quantidade de códigos individuais. Continuar?')) {
          return;
        }
      }
      
      setLoading(true);
      
      const payload = {
        instituicaoId,
        tipoConvite: formGerador.tipoConvite,
        quantidade: parseInt(formGerador.quantidade, 10),
        modulosAcesso: formGerador.modulosAcesso.length > 0 ? formGerador.modulosAcesso : null,
        dataExpiracao: formGerador.dataExpiracao ? new Date(formGerador.dataExpiracao).toISOString() : null
      };
      
      const response = await api.post('/convites', payload);
      
      enqueueSnackbar('Códigos gerados com sucesso!', { variant: 'success' });
      
      // Resetar o formulário
      setFormGerador({
        tipoConvite: 'INDIVIDUAL',
        quantidade: 1,
        dataExpiracao: '',
        modulosAcesso: []
      });
      
      // Recarregar os convites
      const convitesResponse = await api.get(`/convites/instituicao/${instituicaoId}`);
      setConvites(convitesResponse.data.convites || convitesResponse.data);
      setTotalConvites(convitesResponse.data.total || convitesResponse.data.length);
      
      // Fechar o diálogo
      setDialogOpen(false);
    } catch (error) {
      console.error('Erro ao gerar códigos:', error);
      enqueueSnackbar(error.response?.data?.mensagem || 'Erro ao gerar códigos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Função para cancelar um convite
  const cancelarConvite = async () => {
    if (!conviteSelecionado) return;
    
    try {
      setLoading(true);
      
      await api.patch(`/convites/${conviteSelecionado.id}/cancelar`);
      
      enqueueSnackbar('Convite cancelado com sucesso!', { variant: 'success' });
      
      // Atualizar o status do convite na lista local
      setConvites(prev => 
        prev.map(c => 
          c.id === conviteSelecionado.id 
            ? { ...c, status: 'CANCELADO' } 
            : c
        )
      );
      
      setCancelarDialogOpen(false);
      setConviteSelecionado(null);
    } catch (error) {
      console.error('Erro ao cancelar convite:', error);
      enqueueSnackbar(error.response?.data?.mensagem || 'Erro ao cancelar convite', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Função para copiar código para a área de transferência
  const copiarCodigo = (codigo) => {
    navigator.clipboard.writeText(codigo)
      .then(() => {
        enqueueSnackbar('Código copiado para a área de transferência!', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar('Erro ao copiar código', { variant: 'error' });
      });
  };
  
  // Função para exportar códigos como CSV
  const exportarCodigos = () => {
    // Preparar dados para CSV
    const header = 'Código,Tipo,Status,Data Criação,Data Expiração\n';
    const rows = convites.map(c => {
      const dataCriacao = c.createdAt ? format(new Date(c.createdAt), 'dd/MM/yyyy', { locale: ptBR }) : '';
      const dataExpiracao = c.dataExpiracao ? format(new Date(c.dataExpiracao), 'dd/MM/yyyy', { locale: ptBR }) : '';
      
      return `${c.codigo},${c.tipoConvite},${c.status},${dataCriacao},${dataExpiracao}`;
    }).join('\n');
    
    const csv = header + rows;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `convites_${instituicaoId}_${new Date().toISOString().split('T')[0]}.csv`);
    
    enqueueSnackbar('Códigos exportados com sucesso!', { variant: 'success' });
  };
  
  // Renderizar o chip de status
  const renderStatusChip = (status, usado) => {
    let color;
    let label = status;
    
    switch (status) {
      case 'ATIVO':
        color = usado ? 'success' : 'primary';
        label = usado ? 'USADO' : 'ATIVO';
        break;
      case 'CANCELADO':
        color = 'error';
        break;
      case 'EXPIRADO':
        color = 'warning';
        break;
      case 'USADO':
        color = 'success';
        break;
      default:
        color = 'default';
    }
    
    return <Chip label={label} color={color} size="small" />;
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        Gerador de Códigos de Convite
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Gerar Novos Códigos
        </Typography>
        
        <Box component="form" noValidate sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Convite</InputLabel>
                <Select
                  name="tipoConvite"
                  value={formGerador.tipoConvite}
                  onChange={handleFormChange}
                  label="Tipo de Convite"
                >
                  <MenuItem value="INDIVIDUAL">Individual (uso único)</MenuItem>
                  <MenuItem value="LOTE">Lote (múltiplos usos)</MenuItem>
                  <MenuItem value="GERAL">Geral (ilimitado)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {formGerador.tipoConvite !== 'GERAL' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Quantidade"
                  name="quantidade"
                  type="number"
                  value={formGerador.quantidade}
                  onChange={handleFormChange}
                  InputProps={{ inputProps: { min: 1, max: 100 } }}
                />
              </Grid>
            )}
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Data de Expiração (opcional)"
                name="dataExpiracao"
                type="date"
                value={formGerador.dataExpiracao}
                onChange={handleFormChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={loadingModulos}>
                <InputLabel>Módulos de Acesso</InputLabel>
                <Select
                  multiple
                  name="modulosAcesso"
                  value={formGerador.modulosAcesso}
                  onChange={handleModulosChange}
                  input={<OutlinedInput label="Módulos de Acesso" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const modulo = modulosDisponiveis.find(m => m.codigo === value);
                        return (
                          <Chip 
                            key={value} 
                            label={modulo ? modulo.nome : value} 
                            size="small" 
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {loadingModulos ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> Carregando módulos...
                    </MenuItem>
                  ) : (
                    modulosDisponiveis.map((modulo) => (
                      <MenuItem key={modulo.id} value={modulo.codigo}>
                        <Checkbox 
                          checked={formGerador.modulosAcesso.includes(modulo.codigo)} 
                        />
                        <ListItemText primary={modulo.nome} />
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
              disabled={loading || loadingModulos}
            >
              Gerar Códigos
            </Button>
          </Box>
        </Box>
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Códigos de Convite
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => {/* Implementar filtros */}}
            >
              Filtros
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportarCodigos}
              disabled={convites.length === 0}
            >
              Exportar
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                setPage(0);
                // Recarregar convites
              }}
              disabled={loadingConvites}
            >
              Atualizar
            </Button>
          </Box>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Data Criação</TableCell>
                <TableCell>Data Expiração</TableCell>
                <TableCell>Módulos</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingConvites ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={30} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Carregando convites...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : convites.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2">
                      Nenhum convite encontrado.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                convites.map((convite) => (
                  <TableRow key={convite.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                          {convite.codigo}
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => copiarCodigo(convite.codigo)}
                          sx={{ ml: 1 }}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>{convite.tipoConvite}</TableCell>
                    <TableCell>
                      {renderStatusChip(convite.status, convite.usado)}
                    </TableCell>
                    <TableCell>
                      {convite.createdAt ? 
                        format(new Date(convite.createdAt), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 
                        '-'}
                    </TableCell>
                    <TableCell>
                      {convite.dataExpiracao ? 
                        format(new Date(convite.dataExpiracao), 'dd/MM/yyyy', { locale: ptBR }) : 
                        '-'}
                    </TableCell>
                    <TableCell>
                      {convite.modulosAcesso && Array.isArray(convite.modulosAcesso) && convite.modulosAcesso.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {convite.modulosAcesso.map(codigo => {
                            const modulo = modulosDisponiveis.find(m => m.codigo === codigo);
                            return (
                              <Chip 
                                key={codigo} 
                                label={modulo ? modulo.nome : codigo} 
                                size="small" 
                                variant="outlined"
                              />
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Todos os módulos
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {convite.status === 'ATIVO' && !convite.usado && (
                        <Tooltip title="Cancelar Convite">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => {
                              setConviteSelecionado(convite);
                              setCancelarDialogOpen(true);
                            }}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalConvites}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />
      </Paper>
      
      {/* Diálogo de confirmação para gerar códigos */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Confirmar Geração de Códigos</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Você está prestes a gerar:
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            {formGerador.tipoConvite === 'GERAL' 
              ? '1 código de uso geral (ilimitado)' 
              : `${formGerador.quantidade} código(s) de uso ${formGerador.tipoConvite === 'INDIVIDUAL' ? 'único' : 'múltiplo'}`}
          </Typography>
          
          {formGerador.dataExpiracao && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Expiração: {format(new Date(formGerador.dataExpiracao), 'dd/MM/yyyy', { locale: ptBR })}
            </Typography>
          )}
          
          {formGerador.modulosAcesso.length > 0 && (
            <>
              <Typography variant="body2" sx={{ mt: 2 }}>
                Módulos de acesso:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {formGerador.modulosAcesso.map(codigo => {
                  const modulo = modulosDisponiveis.find(m => m.codigo === codigo);
                  return (
                    <Chip 
                      key={codigo} 
                      label={modulo ? modulo.nome : codigo} 
                      size="small" 
                    />
                  );
                })}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button 
            onClick={gerarCodigos} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Diálogo de confirmação para cancelar convite */}
      <Dialog open={cancelarDialogOpen} onClose={() => setCancelarDialogOpen(false)}>
        <DialogTitle>Cancelar Convite</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Tem certeza que deseja cancelar o convite <strong>{conviteSelecionado?.codigo}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Esta ação não pode ser desfeita. O convite não poderá mais ser utilizado.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelarDialogOpen(false)} disabled={loading}>
            Voltar
          </Button>
          <Button 
            onClick={cancelarConvite} 
            color="error" 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CancelIcon />}
          >
            Cancelar Convite
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GeradorCodigosConvite;